import styled from '@emotion/styled';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { Children, cloneElement, forwardRef, useCallback, useContext, useMemo, useRef, } from 'react';
import { css } from '@emotion/react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useAccordionControls, } from '@polestar/component-warehouse-react/components/accordion/useAccordionControls';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { usePrint } from '@polestar/component-warehouse-react/contexts/print';
import { docgenEnum } from '@polestar/component-warehouse-react/helpers/docgenInterface';
export var AccordionVariants;
(function (AccordionVariants) {
    AccordionVariants["default"] = "default";
    AccordionVariants["titleOnOpen"] = "titleOnOpen";
})(AccordionVariants || (AccordionVariants = {}));
/**
 * @internal
 */
export const AccordionVariantsInterface = docgenEnum('AccordionVariants', AccordionVariants);
/**
 * @internal
 */
export const AccordionContext = React.createContext({
    indented: false,
    id: '',
    scrollIntoView: false,
    scrollIntoViewOffset: 0,
});
const EMPTY_OBJECT = {};
const Accordion = forwardRef(({ id, children, controls, closeOthersOnOpen = false, disabled = false, border = true, variant = AccordionVariants.default, largeIcons, onToggle, onToggleEnded, initialExpanded = EMPTY_OBJECT, scrollIntoView, scrollIntoViewOffset, ...props }, ref) => {
    const _controls = useAccordionControls({
        closeOthersOnOpen,
        initialExpanded,
    });
    const { indented, scrollIntoView: contextScrollIntoView, scrollIntoViewOffset: contextScrollIntoViewOffset, controls: contextControls, } = useContext(AccordionContext);
    const internalControls = controls || contextControls || _controls;
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const memoizedId = useSSRSafeId('accordion', id);
    const { printing } = usePrint();
    const handleToggle = useCallback((id, expanded) => {
        if (id) {
            internalControls.toggle(id);
            onToggle === null || onToggle === void 0 ? void 0 : onToggle(id, expanded);
        }
    }, [onToggle, internalControls]);
    const amountOfChildren = (children === null || children === void 0 ? void 0 : children.length) || 0;
    const contextValue = useMemo(() => {
        var _a, _b;
        return {
            indented: true,
            id: memoizedId,
            scrollIntoView: (_a = scrollIntoView !== null && scrollIntoView !== void 0 ? scrollIntoView : contextScrollIntoView) !== null && _a !== void 0 ? _a : false,
            scrollIntoViewOffset: (_b = scrollIntoViewOffset !== null && scrollIntoViewOffset !== void 0 ? scrollIntoViewOffset : contextScrollIntoViewOffset) !== null && _b !== void 0 ? _b : 0,
            controls: internalControls,
        };
    }, [
        contextScrollIntoView,
        contextScrollIntoViewOffset,
        internalControls,
        memoizedId,
        scrollIntoView,
        scrollIntoViewOffset,
    ]);
    return (React.createElement(Container, { ...aria({
            disabled,
        }), ...props, ref: combinedRef, indented: indented, border: border, printing: printing, disabled: disabled, id: `${memoizedId}-wrapper`, as: "ul" },
        React.createElement(AccordionContext.Provider, { value: contextValue }, children &&
            Children.map(children, (child, index) => {
                const id = child.props.id || `${memoizedId}-${index}`;
                if (child.props.expanded !== undefined) {
                    // eslint-disable-next-line no-console
                    console.warn('You are trying to use AccordionSection as a controlled element inside Accordion. ' +
                        'Please you the `initialExpanded` prop on Accordion instead in combination with an id on the AccordionSection');
                }
                return cloneElement(child, {
                    ...child.props,
                    variant,
                    largeIcon: largeIcons,
                    id,
                    childOnToggle: child.props.onToggle,
                    onToggle: handleToggle,
                    onToggleEnded,
                    expanded: printing ? true : internalControls.isExpanded(id),
                    disabled: disabled || child.props.disabled,
                    // only trigger when at least 1 section is expanded
                    border: !indented || (indented && index < amountOfChildren - 1),
                    scrollIntoView: contextValue.scrollIntoView,
                    scrollIntoViewOffset: contextValue.scrollIntoViewOffset,
                });
            }))));
});
Accordion.displayName = 'Accordion';
export default Accordion;
const Container = styled.div(({ theme, indented, border, printing, disabled }) => {
    return css(css `
      padding-inline-start: ${indented ? Spacings.medium : 0};
      margin-block-end: -${indented ? Spacings.large : 0};
      position: relative;
    `, border &&
        indented &&
        !printing &&
        css `
        &:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: ${createCustomSpacing(1 / 16)};
          background: ${theme.accordion.states[disabled ? 'disabled' : 'enabled'].borderColor};
        }
      `, border &&
        indented &&
        printing &&
        css `
        &:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: ${createCustomSpacing(1 / 16)};
          background: ${theme.accordion.states[disabled ? 'disabled' : 'enabled'].borderColor};
        }
      `);
});
